import { Component, inject, OnInit } from '@angular/core';
import { IntegrationAppsService } from '../../integration-apps.service';
import { firstValueFrom } from 'rxjs';
import { GetPbxIntegrationAppInstallationsRequest, PbxIntegrationAppInstallation, SlimIntegrationApp } from '@proto/odoo/integrations';
import { SubscriptionService } from 'src/app/subscription/subscription.service';
import { Subscription } from 'src/app/subscription/subscription.model';
import { ToastService } from 'src/app/toast.service';
import { I18NextService } from 'angular-i18next';

@Component({
  selector: 'app-integration-apps-hub',
  templateUrl: './integration-apps-hub.component.html',
  standalone: false
})
export class IntegrationAppsHubComponent implements OnInit {

  private integrationService = inject(IntegrationAppsService);
  private subscriptionService = inject(SubscriptionService);
  private toastService = inject(ToastService);
  private i18Next = inject(I18NextService);

  private subscriptions: Subscription[] = [];
  public privateApps: SlimIntegrationApp[] = [];
  public appInstallations: PbxIntegrationAppInstallation[] = [];

  public installedAppsInitial: IntegrationAppWithInstallations[] = [];
  public installedAppsToDisplay: IntegrationAppWithInstallations[] = [];

  ngOnInit() {
    this.fetchHubApps();
  }

  async fetchHubApps() {
    try {
      this.subscriptions = await firstValueFrom(this.subscriptionService.getSubscriptions());

      const privateAppListResponse = await firstValueFrom(this.integrationService.getPrivateApps());
      this.privateApps = privateAppListResponse.apps;

      const req = GetPbxIntegrationAppInstallationsRequest.create({});
      this.appInstallations = (await firstValueFrom(this.integrationService.getAppInstallations(req))).installations;

    } catch (error) {
      this.toastService.error(this.i18Next.t('integration_apps_load_resources_error'), 'error');
      return;
    }

    const installedApps = new Map<string, IntegrationAppWithInstallations>();

    for (const pbxInstallation of this.appInstallations) {
      const installation = pbxInstallation.installation;
      const app = installation?.app;

      if (!app?.uuid) {
        continue;
      }

      const applianceId = String(pbxInstallation.applianceId);
      const subscription = this.subscriptions.find(
        sub => String(sub.appliance_id) === applianceId
      );

      // ignore installations for subscriptions that are not available here
      if (!subscription) {
        continue;
      }

      let appWithInstallations = installedApps.get(app.uuid);
      if (!appWithInstallations) {
        appWithInstallations = {
          slimIntegrationApp: app,
          appliances: []
        };
      } else {
        // set the latest version of an integration app here
        if (appWithInstallations.slimIntegrationApp.currentVersion < app.currentVersion) {
          appWithInstallations.slimIntegrationApp = app;
        }
      }
      installedApps.set(app.uuid, appWithInstallations);

      if (!appWithInstallations.appliances.some(appliance => appliance.id === applianceId)) {
        appWithInstallations.appliances.push({
          id: applianceId,
          name: subscription.name ?? ''
        });
      }
    }

    const installedIntegrationApps = [...installedApps.values()];
    this.installedAppsInitial = installedIntegrationApps;
    this.installedAppsToDisplay = installedIntegrationApps;
  }

  filter(event: any) {
    this.installedAppsToDisplay = this.installedAppsInitial.filter(app => app.slimIntegrationApp.name.toLowerCase().includes(event.toLowerCase()) || app.slimIntegrationApp.vendor.toLowerCase().includes(event.toLowerCase()));
  }

}

export interface IntegrationAppWithInstallations {
  slimIntegrationApp: SlimIntegrationApp;
  appliances: ApplianceIdentifier[];
}

export type ApplianceIdentifier = {
  id: string;
  name: string;
}